import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import AlertDetailsReportHeader from '../../AlertDetailsReportHeader/AlertDetailsReportHeader';
import AlertDetailsNavTabs from '../../AlertDetailsNavTabs/AlertDetailsNavTabs';
import AlertDetailsModal from '../../AlertDetailsModals/AlertDetailsModal';
import { connectModalContainerContextToComponent } from '../../../../../contexts/ModalContext/ModalContext';
import EditAlertConditionFormWrapper from '../../EditAlertConditionFormWrapper/EditAlertConditionFormWrapper';
import {
  selectSingleAlert,
  selectSingleAlertCrudOperation,
  selectSingleAlertCrudStatus,
  selectSingleAlertErrors,
} from '../../../../../redux/slices/singleAlertSlice/singleAlertSlice';
import { CrudStatus } from '../../../../../utility/types/CrudStatus';
import { CrudType } from '../../../../../utility/types/CrudType';
import useAlertUpdater, { ALERT_DETAILS, ALERT_EDIT_THRESHOLD } from './useAlertUpdater';
import { selectByUuidOrMacAddress } from '../../../../../redux/slices/deviceList/deviceListSlice';
import AlertReading from '../../../AlertThreshold/AlertReading/AlertReading';
import { alertDetailsModalTypes } from '../../../../../utility/alertDetailsModalTypes';

function AlertDetailsActivatedView(props) {
  const {
    hasAdvancedAlerting,
    setModalJSX,
  } = props;

  const activeAlert = useSelector(selectSingleAlert);
  const formErrors = useSelector(selectSingleAlertErrors);

  const {
    handleEditAlertError,
    handleSubmitAlertCondition,
    handleAcknowledgeAlert,
    handleDeleteAlert,
    handleDeleteAlertAction,
    handleUpdateChannelKey,
    toggleEditAlertConditionForm,
    cancelEditCondition,
    handleEditAlertSuccess,
    isFormSubmitting,
    submitBtnText,
    editedAlert,
    setEditedAlert,
    alertDetailsState,
  } = useAlertUpdater();

  const selectedAlert = useSelector(selectSingleAlert);

  const device = useSelector(
    (state) => selectByUuidOrMacAddress(state, selectedAlert.channelKey.macAddress),
  );

  const hasDeviceAssigned = !!device;
  if (!selectedAlert || !hasDeviceAssigned) {
    return (<Redirect to="/s/alerts" />);
  }

  const crudOperation = useSelector(selectSingleAlertCrudOperation);
  const crudStatus = useSelector(selectSingleAlertCrudStatus);

  React.useEffect(() => {
    if (crudOperation === CrudType.UPDATE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleEditAlertSuccess(activeAlert.id);
          break;
        case CrudStatus.ERROR:
          handleEditAlertError();
          break;
        default:
          break;
      }
    }
  }, [crudOperation, crudStatus]);

  const dispatchModalAction = (type, payload) => {
    const jsx = (
      <AlertDetailsModal
        type={type}
        modalPayload={payload}
        handleDeleteAlertAction={handleDeleteAlertAction}
        handleDeleteAlert={handleDeleteAlert}
        handleDeleteAlertComment={() => {}}
        handleAcknowledgeAlert={handleAcknowledgeAlert}
        description={activeAlert.description}
        alertId={activeAlert.id}
      />
    );
    setModalJSX(jsx);
  };

  const renderDetailsMainContent = () => {
    switch (alertDetailsState) {
      case ALERT_EDIT_THRESHOLD:
        return (
          <EditAlertConditionFormWrapper
            isFormSubmitting={isFormSubmitting}
            submitBtnText={submitBtnText}
            handleSubmitAlertCondition={handleSubmitAlertCondition}
            formErrors={formErrors}
            editedAlert={editedAlert}
            setEditedAlert={setEditedAlert}
            handleUpdateChannelKey={handleUpdateChannelKey}
            cancelEditCondition={cancelEditCondition}
          />
        );
      case ALERT_DETAILS:
      default:
        return (
          <AlertReading
            acknowledgeAlert={() => dispatchModalAction(
              alertDetailsModalTypes.ACKNOWLEDGE_ALERT,
            )}
          />
        );
    }
  };

  return (
    <div id="details-container" style={{ marginTop: '20px' }}>
      <AlertDetailsReportHeader
        toggleEditAlertCondition={toggleEditAlertConditionForm}
        dispatchModal={dispatchModalAction}
      />
      {renderDetailsMainContent()}
      {
        activeAlert.isDeviceAssignmentSuspended && (
        <div className="alert alert-warning">
          <div className="float-left">
            <Icon icon="exclamation-circle" />
          </div>
          <div style={{ marginLeft: '1.5em' }}>
            {/* eslint-disable-next-line max-len */}
            The device for this alert has been suspended because you’ve reached your account’s
            device limit.
            {/* eslint-disable-next-line max-len */}
            This alert will not trigger or send any notifications. To reactivate your suspended
            devices,
            <a href="/s/subscription/billing/change-plan" target="_blank">upgrade</a>
            {' '}
            your account.
            {' '}
            <a
              className="btn btn-sm btn-light border-info py-0"
              href="/s/subscription/billing/change-plan"
              target="_blank"
              style={{ lineHeight: '1' }}
            >
              Upgrade Now
            </a>
          </div>
        </div>
        )
      }
      <AlertDetailsNavTabs
        dispatchModalAction={dispatchModalAction}
        hasAdvancedAlerting={hasAdvancedAlerting}
      />
    </div>
  );
}

AlertDetailsActivatedView.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  hasAdvancedAlerting: PropTypes.bool.isRequired,
};

export { AlertDetailsActivatedView };
export default connectModalContainerContextToComponent(
  AlertDetailsActivatedView,
);
